// services/debugService.js - Version corrigée qui s'initialise correctement
import AsyncStorage from '@react-native-async-storage/async-storage';

class DebugService {
  constructor() {
    this.errors = [];
    this.isInitialized = false;
    
    // Initialisation immédiate
    this.init();
  }

  async init() {
    try {
      if (typeof window !== 'undefined' && !this.isInitialized) {
        this.setupErrorHandler();
        this.exposeGlobalTools();
        this.isInitialized = true;
        console.log('🛠️ DebugService initialized successfully');
      }
    } catch (error) {
      console.error('Failed to initialize DebugService:', error);
    }
  }

  // Capturer toutes les erreurs JSON.parse
  setupErrorHandler() {
    if (typeof window !== 'undefined') {
      // Override global JSON.parse pour capturer les erreurs
      const originalJSONParse = JSON.parse;
      
      JSON.parse = (text, reviver) => {
        try {
          // Vérifier si c'est déjà un objet
          if (typeof text === 'object' && text !== null) {
            console.error('🚨 JSON.parse called with object instead of string:', {
              type: typeof text,
              value: text,
              stack: new Error().stack
            });
            
            this.logError('JSON.parse called with object', {
              type: typeof text,
              value: text,
              stack: new Error().stack
            });
            
            // Essayer de retourner l'objet tel quel
            return text;
          }
          
          return originalJSONParse.call(this, text, reviver);
        } catch (error) {
          console.error('🚨 JSON.parse error:', {
            text: text,
            textType: typeof text,
            error: error.message,
            stack: new Error().stack
          });
          
          this.logError('JSON.parse error', {
            text: text,
            textType: typeof text,
            error: error.message,
            stack: new Error().stack
          });
          
          throw error;
        }
      };

      // Capturer les erreurs globales
      window.addEventListener('error', (event) => {
        if (event.message.includes('JSON.parse') || event.message.includes('not valid JSON')) {
          console.error('🚨 Global JSON error caught:', event);
          this.logError('Global JSON error', {
            message: event.message,
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno,
            stack: event.error?.stack
          });
        }
      });

      // Capturer les promesses rejetées
      window.addEventListener('unhandledrejection', (event) => {
        if (event.reason?.message?.includes('JSON') || 
            event.reason?.message?.includes('not valid JSON')) {
          console.error('🚨 Unhandled JSON promise rejection:', event);
          this.logError('Promise rejection JSON error', {
            reason: event.reason.message,
            stack: event.reason.stack
          });
        }
      });
    }
  }

  // Exposer les outils globalement
  exposeGlobalTools() {
    if (typeof window !== 'undefined') {
      window.debugTrials = {
        diagnose: () => this.diagnoseStorage(),
        clean: () => this.cleanCorruptedStorage(),
        errors: () => this.getErrorReport(),
        validate: () => this.validateStoredData(),
        forceMigration: () => this.forceMigration(),
        
        // Nouveaux outils
        checkKey: (key) => this.checkSpecificKey(key),
        listKeys: () => this.listAllKeys(),
        testJSON: (value) => this.testJSONParse(value)
      };
      
      console.log('🛠️ Debug tools enabled: window.debugTrials');
      console.log('Available commands:');
      console.log('  debugTrials.diagnose() - Full storage diagnosis');
      console.log('  debugTrials.clean() - Clean corrupted data');
      console.log('  debugTrials.errors() - Show error report');
      console.log('  debugTrials.validate() - Validate stored data');
      console.log('  debugTrials.checkKey("key") - Check specific key');
      console.log('  debugTrials.listKeys() - List all storage keys');
    }
  }

  // Logger les erreurs avec timestamp
  logError(type, details) {
    const error = {
      timestamp: new Date().toISOString(),
      type,
      details,
      url: typeof window !== 'undefined' ? window.location.href : 'unknown'
    };
    
    this.errors.push(error);
    
    // Garder seulement les 50 dernières erreurs
    if (this.errors.length > 50) {
      this.errors = this.errors.slice(-50);
    }
  }

  // Diagnostic complet du stockage
  async diagnoseStorage() {
    console.log('🔍 === DIAGNOSTIC STORAGE ===');
    
    try {
      // Check AsyncStorage React Native
      if (typeof AsyncStorage !== 'undefined') {
        console.log('🔍 AsyncStorage (React Native) contents:');
        const keys = await AsyncStorage.getAllKeys();
        console.log(`Found ${keys.length} keys in AsyncStorage`);
        
        for (const key of keys.slice(0, 20)) { // Limiter à 20 pour performance
          try {
            const value = await AsyncStorage.getItem(key);
            console.log(`  ${key}:`, typeof value, value?.substring(0, 100));
            
            // Détecter les problèmes
            if (typeof value === 'object' && value !== null) {
              console.warn(`    ⚠️ ${key} is an object instead of string!`);
            }
            if (typeof value === 'string' && value.includes('[object Object]')) {
              console.warn(`    ⚠️ ${key} contains [object Object]!`);
            }
          } catch (keyError) {
            console.error(`    ❌ Error reading ${key}:`, keyError);
          }
        }
      } else {
        console.log('ℹ️ AsyncStorage not available (not React Native environment)');
      }
      
      // Check localStorage si disponible
      if (typeof window !== 'undefined' && window.localStorage) {
        console.log('🔍 localStorage contents:');
        for (let i = 0; i < Math.min(localStorage.length, 20); i++) {
          const key = localStorage.key(i);
          const value = localStorage.getItem(key);
          console.log(`  ${key}:`, typeof value, value?.substring(0, 100));
        }
      }
      
      // Check sessionStorage si disponible
      if (typeof window !== 'undefined' && window.sessionStorage) {
        console.log('🔍 sessionStorage contents:');
        for (let i = 0; i < Math.min(sessionStorage.length, 20); i++) {
          const key = sessionStorage.key(i);
          const value = sessionStorage.getItem(key);
          console.log(`  ${key}:`, typeof value, value?.substring(0, 100));
        }
      }
      
    } catch (error) {
      console.error('Storage diagnosis error:', error);
    }
    
    console.log('🔍 === FIN DIAGNOSTIC ===');
  }

  // Vérifier une clé spécifique
  async checkSpecificKey(key) {
    console.log(`🔍 Checking key: ${key}`);
    
    try {
      if (typeof AsyncStorage !== 'undefined') {
        const value = await AsyncStorage.getItem(key);
        console.log(`AsyncStorage[${key}]:`, typeof value, value);
        
        if (typeof value === 'string' && (value.startsWith('{') || value.startsWith('['))) {
          try {
            const parsed = JSON.parse(value);
            console.log(`Parsed ${key}:`, parsed);
          } catch (parseError) {
            console.error(`JSON parse error for ${key}:`, parseError);
          }
        }
      }
      
      if (typeof window !== 'undefined' && window.localStorage) {
        const localValue = localStorage.getItem(key);
        if (localValue) {
          console.log(`localStorage[${key}]:`, typeof localValue, localValue);
        }
      }
      
    } catch (error) {
      console.error(`Error checking key ${key}:`, error);
    }
  }

  // Lister toutes les clés
  async listAllKeys() {
    console.log('📋 All storage keys:');
    
    const allKeys = {
      asyncStorage: [],
      localStorage: [],
      sessionStorage: []
    };
    
    try {
      if (typeof AsyncStorage !== 'undefined') {
        allKeys.asyncStorage = await AsyncStorage.getAllKeys();
      }
      
      if (typeof window !== 'undefined') {
        if (window.localStorage) {
          for (let i = 0; i < localStorage.length; i++) {
            allKeys.localStorage.push(localStorage.key(i));
          }
        }
        
        if (window.sessionStorage) {
          for (let i = 0; i < sessionStorage.length; i++) {
            allKeys.sessionStorage.push(sessionStorage.key(i));
          }
        }
      }
      
      console.log('Storage keys:', allKeys);
      return allKeys;
      
    } catch (error) {
      console.error('Error listing keys:', error);
      return allKeys;
    }
  }

  // Test de parsing JSON sécurisé
  testJSONParse(value) {
    console.log('🧪 Testing JSON parse for:', typeof value, value);
    
    try {
      if (typeof value === 'object' && value !== null) {
        console.log('✅ Already an object, returning as-is');
        return value;
      }
      
      if (!value) {
        console.log('✅ Empty value, returning null');
        return null;
      }
      
      const parsed = JSON.parse(value);
      console.log('✅ JSON parse successful:', parsed);
      return parsed;
      
    } catch (error) {
      console.warn('⚠️ JSON parse failed:', error.message);
      return value;
    }
  }

  // Nettoyer tout le stockage corrompu
  async cleanCorruptedStorage() {
    console.log('🧹 Cleaning potentially corrupted storage...');
    
    let cleanedCount = 0;
    
    try {
      // Test et nettoyage AsyncStorage
      if (typeof AsyncStorage !== 'undefined') {
        const keys = await AsyncStorage.getAllKeys();
        
        for (const key of keys) {
          try {
            const value = await AsyncStorage.getItem(key);
            
            // Supprimer si c'est un objet au lieu d'une string
            if (typeof value === 'object' && value !== null) {
              console.warn(`🧹 Removing object key: ${key}`);
              await AsyncStorage.removeItem(key);
              cleanedCount++;
              continue;
            }
            
            // Supprimer si contient [object Object]
            if (typeof value === 'string' && value.includes('[object Object]')) {
              console.warn(`🧹 Removing [object Object] key: ${key}`);
              await AsyncStorage.removeItem(key);
              cleanedCount++;
              continue;
            }
            
            // Tester le JSON parsing
            if (typeof value === 'string' && (value.startsWith('{') || value.startsWith('['))) {
              try {
                JSON.parse(value);
              } catch (parseError) {
                console.warn(`🧹 Removing invalid JSON key: ${key}`);
                await AsyncStorage.removeItem(key);
                cleanedCount++;
              }
            }
            
          } catch (error) {
            console.warn(`🧹 Error checking key ${key}, removing:`, error);
            await AsyncStorage.removeItem(key);
            cleanedCount++;
          }
        }
      }
      
      // Nettoyer localStorage si disponible
      if (typeof window !== 'undefined' && window.localStorage) {
        const keysToCheck = [];
        for (let i = 0; i < localStorage.length; i++) {
          keysToCheck.push(localStorage.key(i));
        }
        
        for (const key of keysToCheck) {
          try {
            const value = localStorage.getItem(key);
            if (value && value.includes('[object Object]')) {
              console.warn(`🧹 Removing corrupted localStorage key: ${key}`);
              localStorage.removeItem(key);
              cleanedCount++;
            }
          } catch (error) {
            console.warn(`🧹 Removing problematic localStorage key: ${key}`);
            localStorage.removeItem(key);
            cleanedCount++;
          }
        }
      }
      
      console.log(`🧹 Storage cleanup completed: ${cleanedCount} items cleaned`);
      return cleanedCount;
    } catch (error) {
      console.error('Storage cleanup error:', error);
      return cleanedCount;
    }
  }

  // Obtenir le rapport d'erreurs
  getErrorReport() {
    return {
      totalErrors: this.errors.length,
      recentErrors: this.errors.slice(-10),
      errorTypes: this.errors.reduce((acc, error) => {
        acc[error.type] = (acc[error.type] || 0) + 1;
        return acc;
      }, {})
    };
  }

  // Test de validation des données
  async validateStoredData() {
    console.log('✅ Validating stored data...');
    const report = {
      valid: [],
      invalid: [],
      missing: []
    };
    
    const keysToValidate = [
      { key: 'authToken', type: 'string' },
      { key: 'userEmail', type: 'string' },
      { key: 'competitions', type: 'array' },
      { key: 'hapticType', type: 'string' },
      { key: 'hapticEnabled', type: 'boolean' }
    ];
    
    for (const { key, type } of keysToValidate) {
      try {
        if (typeof AsyncStorage !== 'undefined') {
          const value = await AsyncStorage.getItem(key);
          
          if (value === null || value === undefined) {
            report.missing.push(key);
          } else {
            // Essayer de parser si c'est une string JSON
            let parsedValue = value;
            if (typeof value === 'string' && (value.startsWith('{') || value.startsWith('['))) {
              try {
                parsedValue = JSON.parse(value);
              } catch (parseError) {
                report.invalid.push({ key, error: 'Invalid JSON', value });
                continue;
              }
            }
            
            // Valider le type
            if (type === 'array' && Array.isArray(parsedValue)) {
              report.valid.push(key);
            } else if (type === 'boolean' && typeof parsedValue === 'boolean') {
              report.valid.push(key);
            } else if (type === 'string' && typeof parsedValue === 'string') {
              report.valid.push(key);
            } else {
              report.invalid.push({ key, expectedType: type, actualType: typeof parsedValue, value: parsedValue });
            }
          }
        }
      } catch (error) {
        report.invalid.push({ key, error: error.message });
      }
    }
    
    console.log('✅ Validation report:', report);
    return report;
  }

  // Force migration (placeholder - sera implémenté avec le service de migration)
  async forceMigration() {
    console.log('🔄 Force migration not implemented yet');
    console.log('Use manualDebug.clean() instead');
  }
}

// Instance singleton
const debugService = new DebugService();

// Auto-export pour les tools globaux (s'exécute immédiatement)
export const enableGlobalDebugging = () => {
  // Cette fonction est maintenant vide car l'initialisation se fait automatiquement
  console.log('🛠️ Global debugging already enabled');
};

export default debugService;